import {
  ChangeDetectorRef,
  Component,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  NgZone,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { filter, fromEvent, takeUntil, takeWhile } from 'rxjs';
import Popper from 'popper.js';

@Component({
  selector: 'kt-settings-select',
  templateUrl: './settings-select.component.html',
  styleUrls: ['./settings-select.component.scss'],
  standalone: false,
})
export class SettingsSelectComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() width: number;
  @Output() valueChange = new EventEmitter();
  @Output() onClose = new EventEmitter();

  displayText: string;
  selected: any;
  view: EmbeddedViewRef<any>;
  popperRef: Popper;

  _value: any;
  get value() {
    return this._value;
  }
  set value($event) {
    this._value = $event;
    const matched = this.options.find((opt) => opt.value === this.value);
    if (matched) {
      this.selected = matched;
      this.displayText = matched.label;
    }
    this.cdr.detectChanges();
  }

  constructor(
    @Self()
    @Optional()
    private readonly control: NgControl,
    private readonly zone: NgZone,
    private readonly vcr: ViewContainerRef,
    private readonly cdr: ChangeDetectorRef,
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  onChange = (value) => {};

  onTouched = () => {};

  writeValue(value) {
    this.value = value;
  }
  registerOnChange(onChange) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched) {
    this.onTouched = onTouched;
  }

  isOpen(): boolean {
    return !!this.popperRef;
  }

  open(dropdownTpl: TemplateRef<any>, input: HTMLElement) {
    if (!!this.popperRef) {
      this.close();
    }

    if (this.options.length) {
      this.view = this.vcr.createEmbeddedView(dropdownTpl);
      const dropdown = this.view.rootNodes[0];
      document.body.appendChild(dropdown);
      dropdown.style['z-index'] = '1';
      dropdown.style['margin-top'] = '7px';
      dropdown.style['min-width'] = `${this.width}px`;

      this.zone.runOutsideAngular(() => {
        this.popperRef = new Popper(input, dropdown, {
          placement: 'bottom',
          removeOnDestroy: true,
          modifiers: {
            preventOverflow: { escapeWithReference: true },
          },
        });
      });
    }

    this.handleCloseEvent();
  }

  handleCloseEvent() {
    fromEvent(document, 'click')
      .pipe(
        takeWhile(() => this.isOpen()),
        filter(({ target }) => {
          const origin = this.popperRef.reference as HTMLElement;
          return origin.contains(target as HTMLElement) === false;
        }),
        takeUntil(this.onClose),
      )
      .subscribe(() => {
        this.close();
        this.cdr.detectChanges();
      });
  }

  select(option) {
    if (this.selected !== option) {
      const value = option ? option.value : null;
      this.selected = option;
      this.displayText = option.label;
      this.writeValue(value);
      this.onChange(this.value);
      this.close();
      this.cdr.detectChanges();
    }
  }

  close() {
    this.onClose.emit();
    if (this.view) {
      this.view.destroy();
      this.view = null;
    }
    if (this.popperRef) {
      this.popperRef?.destroy();
      this.popperRef = null;
    }
  }
}
